import { useEffect, useId, useRef } from 'react';
import { euro } from '../format';
import { settlementFor } from '../pos/correction';

/**
 * Kundenansicht (K7): Vollbild mit großer Summe, nach dem Kassieren mit dem Rückgeld. Das Handy liegt zwischen Kasse und
 * Kunde, deshalb ist der Inhalt auf Wunsch um 180° gedreht. Rein zur Anzeige: nichts wird gespeichert oder gebucht. Ein Tipp
 * auf die Fläche schließt; „Drehen“ und „Schließen“ sind für die Kasse, bleiben aufrecht und tippen nicht durch.
 */
export function CustomerView({
  totalCents,
  changeCents,
  paid,
  promoPercent = null,
  alreadyPaidCents = null,
  rotated,
  onRotate,
  onClose,
}: {
  /** Summe des Warenkorbs, nach dem Kassieren die Summe des Verkaufs. */
  totalCents: number;
  /** Rückgeld nach dem Kassieren; `null` ohne Betrag. */
  changeCents: number | null;
  /** Der Verkauf ist kassiert: Rückgeld statt Summe. */
  paid: boolean;
  /** Läuft eine Rabattaktion, steht ihr Prozentsatz da. */
  promoPercent?: number | null;
  /**
   * Korrektur eines Verkaufs (K5): Was der Kunde für den stornierten Verkauf schon bezahlt hat. Vor dem Kassieren steht
   * dann groß, was er noch zahlt oder zurückbekommt, sonst zahlte er womöglich die volle Summe ein zweites Mal.
   */
  alreadyPaidCents?: number | null;
  rotated: boolean;
  onRotate: () => void;
  onClose: () => void;
}) {
  const root = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  useEffect(() => {
    close.current = onClose;
  });
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    root.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (opener?.isConnected === true) opener.focus();
    };
  }, []);
  const showChange = paid && changeCents !== null;
  const describedBy = useId();
  const settle =
    !paid && alreadyPaidCents !== null ? settlementFor(totalCents, alreadyPaidCents) : null;
  const label = showChange
    ? 'Rückgeld'
    : paid
      ? 'Bezahlt'
      : settle === null
        ? 'Summe'
        : settle.kind === 'pay'
          ? 'Noch zu zahlen'
          : settle.kind === 'refund'
            ? 'Sie bekommen zurück'
            : 'Nichts mehr zu zahlen';
  const amount = showChange
    ? changeCents
    : settle === null || settle.kind === 'even'
      ? totalCents
      : settle.cents;
  return (
    <div
      ref={root}
      className="customer"
      role="dialog"
      aria-modal="true"
      aria-label="Kundenansicht"
      tabIndex={-1}
    >
      <button
        type="button"
        className={`customer__face${rotated ? ' customer__face--rotated' : ''}`}
        aria-label="Kundenansicht schließen"
        aria-describedby={describedBy}
        onClick={onClose}
      >
        <span id={describedBy} className="customer__text">
          <span className="customer__label">{label}</span>{' '}
          <span className="customer__amount">{euro(amount)}</span>
          {showChange && <span className="customer__sub">{`Summe ${euro(totalCents)}`}</span>}
          {settle !== null && alreadyPaidCents !== null && (
            <span className="customer__sub">{`Summe ${euro(totalCents)} · schon bezahlt ${euro(alreadyPaidCents)}`}</span>
          )}
        </span>
        {promoPercent !== null && (
          <span className="customer__promo">{`Aktion −${String(promoPercent)} %`}</span>
        )}
      </button>
      <div className="customer__tools">
        <button
          type="button"
          className="btn btn--secondary"
          aria-pressed={rotated}
          onClick={onRotate}
        >
          {rotated ? 'Aufrecht stellen' : 'Drehen'}
        </button>
        <button type="button" className="btn btn--secondary" onClick={onClose}>
          Schließen
        </button>
      </div>
    </div>
  );
}
